import { BiDashboardFilters } from '@euler/model/bi/dashboard';
import { useCallback, useMemo, useState } from 'react';
import { useBiDashboardChat } from '../../dashboard/chat/useBiDashboardChat';
import { BiEditorTarget } from '../../dashboard/editor/functions/biEditorState';
import { BiCardSave } from '../../dashboard/editor/functions/useBiCardEditor';
import { useBiCardData } from '../../dashboard/functions/useBiCardData';
import { useBiCardEditing } from '../../dashboard/functions/useBiCardEditing';
import { useBiChatUndo } from '../../dashboard/functions/useBiChatUndo';
import { useBiDashboard } from '../../dashboard/functions/useBiDashboard';
import { useBiGhostHost } from '../../dashboard/ghost/functions/useBiGhostHost';
import { biCanEdit } from '../../functions/biCanEdit';

/**
 * A card's page: its dashboard (for permission, palette, filters and every
 * write), the card, its data under the viewer's date choice (the one the
 * dashboard was showing, or the saved filters), the form's target, the
 * save, the undo toast and the page's chat.
 */
export function useBiCardDetail(
  dashboardId: string,
  cardId: string,
  /** The chat sits beside the page rather than over it. */
  chatBeside: boolean,
  /** The request's filters; undefined applies the dashboard's saved ones. */
  filters: BiDashboardFilters | undefined,
) {
  const { state, refresh, apply, undo, settleChatTap, toast, dismissToast } =
    useBiDashboard(dashboardId);
  const dashboard = state.kind === 'ready' ? state.dashboard : null;
  const card = dashboard?.cards.find(entry => entry.id === cardId) ?? null;
  const { state: data, retry } = useBiCardData(
    card?.id ?? '',
    card?.updatedAt ?? '',
    filters,
    dashboard?.filters ?? {},
  );
  const canEdit = !!dashboard && biCanEdit(dashboard.permission);
  // A role that cannot read the card's fields cannot edit them either, so
  // the form waits until the data says whether it may.
  const canConfigure =
    canEdit && !!card && (data.kind === 'ready' || data.kind === 'failed');
  // Cancel starts the form again from the saved card.
  const [reset, setReset] = useState(0);
  const revision = dashboard?.revision ?? 0;
  // The form opens on the card as it was then: a later change to the
  // dashboard (a chat edit, another card) keeps what the user typed, and a
  // save over a changed card is refused as stale. Saved or cancelled, it
  // starts again from the card as it is now.
  // The filters the card is viewed under, which the form's preview uses.
  const viewed = useMemo(
    () => filters ?? dashboard?.filters ?? {},
    [filters, dashboard?.filters],
  );
  const target = useMemo<BiEditorTarget | null>(
    () =>
      card && canConfigure
        ? {
            kind: 'edit',
            card,
            revision,
            loaded:
              data.kind === 'ready'
                ? { result: data.result.current, filters: viewed }
                : undefined,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [card?.id, canConfigure, reset],
  );
  const cancel = useCallback(() => setReset(value => value + 1), []);
  const saveEdit = useBiCardEditing(apply);
  const save = useCallback<BiCardSave>(
    async input => {
      if (!target) return { ok: true };
      const saved = await saveEdit(target, input);
      if (saved.ok) cancel();
      return saved;
    },
    [target, saveEdit, cancel],
  );
  const chat = useBiDashboardChat(dashboardId, settleChatTap, cardId);
  // The chat's change cards see this dashboard; the page draws no ghosts,
  // an applied change shows on the card itself.
  const { setVisible: setChatVisible } = chat;
  // A full-screen chat closes to show the card a change landed on.
  const reveal = useMemo(
    () => (chatBeside ? null : () => setChatVisible(false)),
    [chatBeside, setChatVisible],
  );
  const { host } = useBiGhostHost(
    dashboardId,
    dashboard?.revision ?? null,
    reveal,
  );
  const { onUndo, canUndo } = useBiChatUndo({
    dashboardId,
    toast,
    chatBusy: chat.state.busy,
    answer: chat.answer,
    undo,
  });
  return {
    state,
    refresh,
    dashboard,
    card,
    data,
    retry,
    canEdit,
    canConfigure,
    target,
    viewed,
    cancel,
    save,
    chat,
    host,
    toast,
    dismissToast,
    onUndo,
    canUndo,
  };
}
